import { BESIDE_HANDLE_BASE, BESIDE_HANDLES } from "../characters.ts";

/**
 * The nameplate pool (P1.9-T16; ADR-061 decision 3, "canvas UI allocates nothing per frame"): a plate is taken
 * when an entity appears in the client's view, kept for it while it stays, and returned when it leaves, and a
 * returned plate is the next entity's before the factory makes another. So the plates the UI layer holds are
 * as many as the most players ever in view at once, and a frame of steady play takes and returns nothing.
 *
 * Bookkeeping is by `u16` handle in preallocated arrays: which slot a handle holds, which frame last saw each
 * slot, and the free slots as a stack, so taking, finding and returning a plate allocate nothing once the pool
 * has grown to its size.
 *
 * @example
 * ```ts
 * const plates = new NameplatePool(() => makePlate());
 * const plate = plates.take(handle, frame);
 * plates.sweep(frame, (gone) => gone.hide());
 * ```
 */

/**
 * The keys a plate is taken under: every handle a snapshot can carry, then the other maps' figures
 * (`BESIDE_HANDLE_BASE`).
 */
const HANDLES = BESIDE_HANDLE_BASE + BESIDE_HANDLES;

/**
 * A pool of plates, one per entity in view.
 */
export class NameplatePool<T> {
  private readonly slotOf = new Int32Array(HANDLES).fill(-1);
  private readonly plates: T[] = [];
  private readonly owners: number[] = [];
  private readonly seen: number[] = [];
  private readonly spare: number[] = [];
  private readonly inUse: number[] = [];

  /**
   * @param create Makes a new plate, when an entity appears and no returned plate waits.
   */
  public constructor(private readonly create: () => T) {}

  /**
   * Plates held by entities now.
   *
   * @returns The count.
   */
  public get active(): number {
    return this.inUse.length;
  }

  /**
   * Returned plates waiting for the next entity.
   *
   * @returns The count.
   */
  public get free(): number {
    return this.spare.length;
  }

  /**
   * Plates the factory has made, the most entities in view at once so far.
   *
   * @returns The count.
   */
  public get created(): number {
    return this.plates.length;
  }

  /**
   * The plate an entity holds.
   *
   * @param handle The entity's handle.
   * @returns Its plate, or undefined when it holds none.
   */
  public get(handle: number): T | undefined {
    if (!Number.isInteger(handle) || handle < 0 || handle >= HANDLES) {
      return undefined;
    }
    const slot = this.slotOf[handle] as number;
    return slot < 0 ? undefined : this.plates[slot];
  }

  /**
   * The plate for an entity seen on a frame: the one it holds, else a returned one, else a new one.
   *
   * @param handle The entity's handle.
   * @param frame The frame it was seen on.
   * @returns Its plate.
   * @throws RangeError when the handle is not one a snapshot carries (0 to 65535) nor one of the other maps' figures'
   *   keys (`BESIDE_HANDLE_BASE` on).
   */
  public take(handle: number, frame: number): T {
    if (!Number.isInteger(handle) || handle < 0 || handle >= HANDLES) {
      throw new RangeError(`handle ${handle} is not one a snapshot carries`);
    }
    let slot = this.slotOf[handle] as number;
    if (slot < 0) {
      slot = this.spare.pop() ?? this.grow();
      this.slotOf[handle] = slot;
      this.owners[slot] = handle;
      this.inUse.push(slot);
    }
    this.seen[slot] = frame;
    return this.plates[slot] as T;
  }

  /**
   * Returns the plate of every entity not seen on a frame, as each leaves the client's view.
   *
   * @param frame The frame just drawn.
   * @param release Told each plate returned and the handle that held it, so it can hide the plate.
   */
  public sweep(frame: number, release: (plate: T, handle: number) => void): void {
    for (let index = this.inUse.length - 1; index >= 0; index -= 1) {
      const slot = this.inUse[index] as number;
      if (this.seen[slot] === frame) {
        continue;
      }
      const handle = this.owners[slot] as number;
      this.slotOf[handle] = -1;
      this.owners[slot] = -1;
      const last = this.inUse.pop() as number;
      if (last !== slot) {
        this.inUse[index] = last;
      }
      this.spare.push(slot);
      release(this.plates[slot] as T, handle);
    }
  }

  /**
   * Visits every plate an entity holds.
   *
   * @param visit Told each plate and its entity's handle.
   */
  public forEachActive(visit: (plate: T, handle: number) => void): void {
    for (let index = 0; index < this.inUse.length; index += 1) {
      const slot = this.inUse[index] as number;
      visit(this.plates[slot] as T, this.owners[slot] as number);
    }
  }

  /**
   * Makes a plate in a new slot.
   *
   * @returns The slot.
   */
  private grow(): number {
    const slot = this.plates.length;
    this.plates.push(this.create());
    this.owners.push(-1);
    this.seen.push(0);
    return slot;
  }
}
